import React, { useState, useEffect } from 'react';
import { isCampaignOrder } from '../../utils/auditCampaign';
import { hasAnyIdentity, memberDisplayName } from '../../utils/members';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, storage, auth } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { H1, H2 } from '../../components/typography';
import { ArrowLeft, Building, Users, FileText, Edit, Trash2, Plus, Search, AlertTriangle, Paperclip, FileSearch, Upload, Download, Clock, CheckCircle, Link2, Copy, QrCode } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { publicAssociationUrl } from '../../utils/associationSlug';
import { mergeActivatedMembers, isActivatedProfile, withoutEmptyDuplicates } from '../../utils/memberActivation';
import AttachmentsSection from '../../components/AttachmentsSection';
import BoardAdminsPanel from '../../components/BoardAdminsPanel';
import { Dialog } from '@headlessui/react';
import { useToast } from '../../components/ui/ToastProvider';
import EditApplicationModal from '../../components/modals/EditApplicationModal';
import DeleteApplicationModal from '../../components/modals/DeleteApplicationModal';
import AddRentalModal from '../../components/modals/AddRentalModal';
import EditApartmentModal from '../../components/modals/EditApartmentModal';
import { resolveOwnerName, pickHousingForApplication } from '../../utils/applicationOwner';
import { rentalBadge } from '../../utils/rentalPhase';

export default function AssociationDetail() {
  const { associationId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [association, setAssociation] = useState(null);
  const [housing, setHousing] = useState([]);
  const [apartmentCount, setApartmentCount] = useState(0);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('grundinfo');
  const [editModal, setEditModal] = useState({ open: false, application: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, application: null });
  const [addRentalModal, setAddRentalModal] = useState(false);
  const [auditOrders, setAuditOrders] = useState([]);
  const [uploadingOrderId, setUploadingOrderId] = useState(null);

  // New state for apartment editing and association deletion
  const [housingSearchTerm, setHousingSearchTerm] = useState('');
  const [editApartmentModal, setEditApartmentModal] = useState({ open: false, apartment: null });
  const [addApartmentModal, setAddApartmentModal] = useState(false);
  const [deleteAssociationModal, setDeleteAssociationModal] = useState(false);
  const [attachmentsModal, setAttachmentsModal] = useState({ open: false, application: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAssociation, setDeletingAssociation] = useState(false);
  const [deleteMemberModal, setDeleteMemberModal] = useState({ open: false, apartment: null });
  const [deletingMember, setDeletingMember] = useState(false);
  const [activatedProfiles, setActivatedProfiles] = useState([]);
  // Föreningens styrelsekonton (users med rollen brfadmin).
  const [boardAdmins, setBoardAdmins] = useState([]);
  const [activationSearch, setActivationSearch] = useState('');

  useEffect(() => {
    if (associationId) {
      fetchAssociationData();
    }
  }, [associationId]);

  const fetchAssociationData = async () => {
    setLoading(true);
    try {
      // Hämta föreningsdata
      const associationRef = doc(db, 'associations', associationId);
      const associationSnap = await getDoc(associationRef);
      
      if (!associationSnap.exists()) {
        toast({ type: 'error', message: 'Förening hittades inte' });
        navigate('/hyria-dashboard/associations');
        return;
      }
      
      setAssociation({ id: associationSnap.id, ...associationSnap.data() });

      // Hämta lägenheter för denna förening
      const housingQuery = query(
        collection(db, 'housing'),
        where('associationId', '==', associationId)
      );
      const housingSnap = await getDocs(housingQuery);
      const housingRaw = housingSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setApartmentCount(housingRaw.length);
      // Flatmappa: en rad per medlem
      const housingData = [];
      for (const h of housingRaw) {
        const members = h.members || [];
        if (members.length === 0) {
          housingData.push({ ...h, memberIndex: 0, member: {}, empty: true });
        } else {
          members.forEach((m, i) => {
            housingData.push({ ...h, memberIndex: i, member: m });
          });
        }
      }
      // Profiler som aktiverat sig via hyria.se, kopplade till lägenhetsraderna där adressen matchar
      const profilesSnap = await getDocs(query(collection(db, 'users'), where('associationId', '==', associationId)));
      const associationUsers = profilesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setBoardAdmins(associationUsers.filter(u => u.role === 'brfadmin'));
      const profiles = associationUsers.filter(isActivatedProfile);
      const merged = mergeActivatedMembers(housingData, profiles);
      setActivatedProfiles([
        ...merged.rows.filter(r => r.activated).map(r => ({ ...r.activated, matchedHousingId: r.id })),
        ...merged.unmatched.map(p => ({ ...p, matchedHousingId: null })),
      ]);
      // En borttagen dubblettmedlem lämnar en tom post för en lägenhet som har
      // en medlem på en annan post — den raden visas inte (Trello 432).
      setHousing(withoutEmptyDuplicates(merged.rows));

      // Hämta ansökningar för denna förening
      const applicationsQuery = query(
        collection(db, 'applications'),
        where('associationId', '==', associationId)
      );
      const applicationsSnap = await getDocs(applicationsQuery);
      let applicationsData = applicationsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Enrich applications med housing och user data
      const enrichedApplications = await Promise.all(
        applicationsData.map(async (app) => {
          // Samma lägenhetsnyckel som överallt annars. Förut jämfördes adress och
          // nummer tecken för tecken, så ett extra mellanslag räckte för att
          // uthyrningen skulle tappa både adress och ägare i listan.
          const matchingHousing = pickHousingForApplication(housingData, app);

          // Lägenhetens adress om den finns — annars uthyrningens egen. Förut
          // blev adressen tom så fort ingen lägenhet matchade.
          const enrichedApp = {
            ...app,
            address: matchingHousing?.address || app.address || null,
            city: matchingHousing?.city || app.city || null
          };

          // Hämta ägarnamn med prioritering:
          // 1. Ansökans egna ownerFirstName/ownerLastName — ögonblicksbilden
          //    från registreringen, så namnet inte "ändras" när housing-datat gör det
          // 2. Om housing har ownedBy -> hämta från users
          // 3. Om housing har members -> använd första medlemmen
          // 4. Om app har createdBy -> hämta från users
          // 5. Fallback: "Okänd ägare"
          enrichedApp.ownerName = await resolveOwnerName({
            application: app,
            housing: matchingHousing,
            fetchUser: async (uid) => {
              const userSnap = await getDoc(doc(db, 'users', uid));
              return userSnap.exists() ? userSnap.data() : null;
            },
          });

          return enrichedApp;
        })
      );

      setApplications(enrichedApplications);

      // Hämta användardata för ägare (createdBy i ansökningar) - behåll för bakåtkompatibilitet
      const userIds = [...new Set(enrichedApplications.map(app => app.createdBy).filter(Boolean))];
      const newUsersData = {};

      if (userIds.length > 0) {
        // Hämta alla användare i batch
        for (const userId of userIds) {
          try {
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              newUsersData[userId] = `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Okänd ägare';
            } else {
              newUsersData[userId] = 'Okänd ägare';
            }
          } catch (error) {
            newUsersData[userId] = 'Okänd ägare';
          }
        }
      }

      // Merge med befintlig users data istället för att skriva över
      setUsers(prevUsers => ({
        ...prevUsers,
        ...newUsersData
      }));

      // Hämta avvikelserapport-beställningar
      const auditOrdersQuery = query(
        collection(db, 'auditReportOrders'),
        where('associationId', '==', associationId)
      );
      const auditOrdersSnap = await getDocs(auditOrdersQuery);
      const fetchedOrders = auditOrdersSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.requestedAt || '').localeCompare(a.requestedAt || ''));
      setAuditOrders(fetchedOrders);

    } catch (error) {
      toast({ type: 'error', message: 'Kunde inte hämta föreningsdata' });
    } finally {
      setLoading(false);
    }
  };

  const activeApplications = applications.filter(app =>
    app.status && (
      app.status.toUpperCase() === 'APPROVED' ||
      app.status.toUpperCase() === 'FINAL_APPROVED' ||
      app.status.toUpperCase() === 'ACTIVE'
    ) && app.status.toUpperCase() !== 'DELETED'
  );

  const handleEditApplication = (application) => {
    setEditModal({ open: true, application });
  };

  const handleDeleteApplication = (application) => {
    setDeleteModal({ open: true, application });
  };

  const handleModalSuccess = () => {
    // Refresh data after edit/delete
    fetchAssociationData();
    toast({ type: 'success', message: 'Uthyrning uppdaterad' });
  };

  // Filtered housing based on search
  const filteredHousing = housing.filter(apt => {
    if (!housingSearchTerm.trim()) return true;
    const search = housingSearchTerm.toLowerCase();
    const m = apt.member || {};
    const memberName = memberDisplayName(m).toLowerCase();
    const legalEntity = (m.legalEntity || '').toLowerCase();
    const memberEmail = (m.email || '').toLowerCase();
    const memberPhone = (m.phone || '').toLowerCase();
    const aptNumber = (apt.apartmentNumber || '').toLowerCase();
    const address = (apt.address || '').toLowerCase();
    const city = (apt.city || '').toLowerCase();

    return (
      memberName.includes(search) ||
      legalEntity.includes(search) ||
      memberEmail.includes(search) ||
      memberPhone.includes(search) ||
      aptNumber.includes(search) ||
      address.includes(search) ||
      city.includes(search)
    );
  });

  // Handle delete association
  const handleDeleteAssociation = async () => {
    if (deleteConfirmText !== association.name) {
      toast({ type: 'error', message: 'Felaktigt föreningsnamn' });
      return;
    }

    setDeletingAssociation(true);
    try {
      const functions = getFunctions();
      const deleteAssociationFn = httpsCallable(functions, 'deleteAssociation');
      await deleteAssociationFn({ associationId });

      toast({ type: 'success', message: 'Förening och all tillhörande data har raderats' });
      navigate('/hyria-dashboard/associations');
    } catch (error) {
      console.error('Error deleting association:', error);
      toast({ type: 'error', message: error.message || 'Kunde inte radera föreningen' });
    } finally {
      setDeletingAssociation(false);
      setDeleteAssociationModal(false);
    }
  };

  // Cascading delete via Cloud Function deleteHousingMember
  const handleDeleteMember = async () => {
    const apartment = deleteMemberModal.apartment;
    if (!apartment) return;
    setDeletingMember(true);
    try {
      const functions = getFunctions(undefined, 'us-central1');
      const fn = httpsCallable(functions, 'deleteHousingMember');
      const res = await fn({
        housingId: apartment.id,
        memberIndex: apartment.memberIndex,
      });
      const counts = res?.data?.deletedCounts || {};
      const parts = ['medlemmen borttagen ur förteckningen'];
      if (counts.applications) parts.push(`${counts.applications} ansökan${counts.applications !== 1 ? 'ar' : ''} arkiverad${counts.applications !== 1 ? 'e' : ''}`);
      if (counts.ownerCleared) parts.push('ägarkopplingen rensad');
      if (counts.claimCleared) parts.push('anspråket rensat');
      toast({ type: 'success', message: `Klart: ${parts.join(', ')}.` });
      await fetchAssociationData();
    } catch (err) {
      toast({ type: 'error', message: `Kunde inte ta bort medlem: ${err.message}` });
    } finally {
      setDeletingMember(false);
      setDeleteMemberModal({ open: false, apartment: null });
    }
  };

  const publicUrl = publicAssociationUrl(association?.slug);
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({ type: 'success', message: 'Länken kopierad' });
    } catch {
      toast({ type: 'error', message: 'Kunde inte kopiera. Markera och kopiera länken manuellt.' });
    }
  };
  const handleDownloadQr = () => {
    const canvas = document.getElementById('brf-public-qr');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `hyria-qr-${association.slug}.png`;
    a.click();
  };

  const tabs = [
    { id: 'grundinfo', label: 'Grundinfo', icon: Building },
    { id: 'lagenheter', label: 'Lägenheter', icon: Users },
    { id: 'uthyrningar', label: 'Godkända uthyrningar', icon: FileText },
    { id: 'avvikelserapport', label: 'Avvikelserapport', icon: FileSearch },
    { id: 'aktiveringar', label: 'Aktiveringar', icon: CheckCircle }
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#62B8E9] mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar föreningsdata...</p>
        </div>
      </div>
    );
  }

  if (!association) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Förening hittades inte</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header med tillbaka-knapp */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/hyria-dashboard/associations')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Tillbaka till föreningar</span>
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <H1 className="mb-2">{association.name}</H1>
            <p className="text-gray-600">Org.nr: {association.organizationNumber}</p>
          </div>
          
          {/* Snabb-statistik */}
          <div className="flex gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#62B8E9]">
                {association?.numberOfApartments || apartmentCount}
              </div>
              <div className="text-sm text-gray-600">Lägenheter</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#62B8E9]">{housing.length}</div>
              <div className="text-sm text-gray-600">Medlemmar</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#62B8E9]">{activeApplications.length}</div>
              <div className="text-sm text-gray-600">Godkända uthyrningar</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-[#62B8E9]">{applications.length}</div>
              <div className="text-sm text-gray-600">Totala ansökningar</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const pendingCount = tab.id === 'avvikelserapport'
              ? auditOrders.filter(o => o.status === 'pending').length
              : 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#62B8E9] text-[#62B8E9]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
                {pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {activeTab === 'grundinfo' && (
          <div>
            <H2 className="mb-4">Grundläggande information</H2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Föreningsinformation</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Namn:</span> {association.name}</div>
                  <div><span className="font-medium">Organisationsnummer:</span> {association.organizationNumber}</div>
                  <div><span className="font-medium">Styrelse-email:</span> {association.boardEmail}</div>
                  <div><span className="font-medium">Status:</span> 
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                      association.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {association.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>
              </div>
              
              {association.contactPerson && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Kontaktperson</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Namn:</span> {association.contactPerson.firstName} {association.contactPerson.lastName}</div>
                    <div><span className="font-medium">E-post:</span> {association.contactPerson.email}</div>
                    <div><span className="font-medium">Telefon:</span> {association.contactPerson.phone}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Styrelsekonton — utan ett kan ingen logga in som styrelse (2026-10-13). */}
            <BoardAdminsPanel
              admins={boardAdmins}
              onAdd={async ({ firstName, lastName, email }) => {
                const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'addBrfAdmin');
                await fn({ firstName, lastName, email, associationId });
                // Sidan laddar om sina data (och rutan börjar om) — beskedet går som toast.
                toast({
                  type: 'success',
                  message: `Styrelsekonto klart för ${email}. Be personen klicka på "Glömt lösenord" på inloggningssidan för att välja lösenord.`,
                });
                await fetchAssociationData();
              }}
              onRemove={async (boardAdmin) => {
                const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'removeBrfAdmin');
                const res = await fn({ adminId: boardAdmin.id });
                const name = [boardAdmin.firstName, boardAdmin.lastName].filter(Boolean).join(' ') || boardAdmin.email;
                toast({
                  type: 'success',
                  message: res?.data?.demotedOnly
                    ? `${name} är inte längre styrelseadmin. Kontot finns kvar som medlem (BankID).`
                    : `${name} är borttagen som styrelseadmin.`,
                });
                await fetchAssociationData();
              }}
            />

            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2"><Link2 size={16} /> Sida på hyria.se</h3>
              {association.slug ? (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-start">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-[#62B8E9] underline break-all">{publicUrl}</a>
                      <button type="button" onClick={handleCopyLink} className="inline-flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <Copy size={14} /> Kopiera
                      </button>
                    </div>
                    <p className="text-gray-600">Skicka länken till föreningen. Medlemmarna aktiverar sig med BankID och hamnar i medlemsförteckningen.</p>
                    <div className="flex gap-6 pt-2">
                      <div><div className="text-2xl font-semibold text-gray-900">{association.stats?.activatedMembers || 0}</div><div className="text-xs text-gray-500">Aktiverade</div></div>
                      <div><div className="text-2xl font-semibold text-gray-900">{association.stats?.infoConfirmed || 0}</div><div className="text-xs text-gray-500">Bekräftat informationen</div></div>
                      {association.numberOfApartments > 0 && (
                        <div><div className="text-2xl font-semibold text-gray-900">{Math.round(((association.stats?.activatedMembers || 0) / association.numberOfApartments) * 100)} %</div><div className="text-xs text-gray-500">av {association.numberOfApartments} lägenheter</div></div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="bg-white p-2 border border-gray-200 rounded-lg">
                      <QRCodeCanvas id="brf-public-qr" value={publicUrl} size={160} level="H" includeMargin />
                    </div>
                    <button type="button" onClick={handleDownloadQr} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <QrCode size={14} /> Ladda ner QR
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-amber-700">Föreningen saknar länk. Generera länkar från föreningslistan.</p>
              )}
            </div>

            {/* Delete Association Button */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-red-600 mb-2">Farliga åtgärder</h3>
              <p className="text-sm text-gray-600 mb-4">
                Att ta bort föreningen raderar all data permanent, inklusive alla lägenheter, ansökningar och användare kopplade till föreningen.
              </p>
              <button
                onClick={() => setDeleteAssociationModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                <Trash2 size={16} />
                Ta bort förening
              </button>
            </div>
          </div>
        )}

        {activeTab === 'lagenheter' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <H2>Medlemmar ({housing.length})</H2>
              <button
                onClick={() => setAddApartmentModal(true)}
                className="flex items-center gap-2 bg-[#62B8E9] hover:bg-[#4A9FD1] text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Plus size={20} />
                Lägg till medlem
              </button>
            </div>

            {/* Search field */}
            <div className="mb-4">
              <div className="relative max-w-md">
                <input
                  type="text"
                  placeholder="Sök lägenhet (nummer, adress, ägare...)"
                  value={housingSearchTerm}
                  onChange={(e) => setHousingSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#62B8E9] focus:border-transparent outline-none transition-all"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              {housingSearchTerm && (
                <div className="mt-2 text-sm text-gray-600">
                  Visar {filteredHousing.length} av {housing.length} medlemmar
                </div>
              )}
            </div>

            {housing.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lägenhet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Adress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ägare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Telefon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Åtgärder
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredHousing.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          {housingSearchTerm ? 'Inga medlemmar matchar din sökning' : 'Inga medlemmar registrerade'}
                        </td>
                      </tr>
                    ) : (
                      filteredHousing.map((apartment) => (
                        <tr
                          key={`${apartment.id}-${apartment.memberIndex}`}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => setEditApartmentModal({ open: true, apartment })}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {apartment.apartmentNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {apartment.address}, {apartment.city}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {apartment.empty ? (
                              <span className="italic text-gray-400 whitespace-nowrap">Lägenhet utan registrerad medlem</span>
                            ) : (
                              memberDisplayName(apartment.member) || 'Ej registrerad'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {apartment.activated?.email || apartment.member?.email || <span className="text-gray-400 italic">—</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {apartment.activated?.phoneNumber || apartment.member?.phone || <span className="text-gray-400 italic">—</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {apartment.activated ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-[#EAF5FC] text-[#2C7BA5]">Aktiverad</span>
                            ) : apartment.empty ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">
                                Ingen medlem
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Registrerad
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditApartmentModal({ open: true, apartment });
                                }}
                                className="p-2 text-[#62B8E9] hover:bg-blue-50 rounded-lg transition-colors"
                                title="Redigera"
                              >
                                <Edit size={16} />
                              </button>
                              {!apartment.empty && hasAnyIdentity(apartment.member) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteMemberModal({ open: true, apartment });
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Ta bort medlem"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Inga medlemmar registrerade för denna förening
              </div>
            )}
          </div>
        )}

        {activeTab === 'uthyrningar' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <H2>Godkända uthyrningar ({activeApplications.length})</H2>
              <button
                onClick={() => setAddRentalModal(true)}
                className="flex items-center gap-2 bg-[#62B8E9] hover:bg-[#4A9FD1] text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Plus size={20} />
                Lägg till uthyrning
              </button>
            </div>
            
            {activeApplications.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lägenhet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ägare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uthyrningsperiod
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Åtgärder
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activeApplications.map((application) => (
                      <tr key={application.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          <div>
                            {application.address && application.city ? (
                              <>
                                <div>{application.address}, Lgh {application.apartmentNumber}</div>
                                <div className="text-gray-500 font-normal">{application.city}</div>
                              </>
                            ) : (
                              <div>Lgh {application.apartmentNumber}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {application.ownerName ||
                           (application.ownerFirstName && application.ownerLastName
                            ? `${application.ownerFirstName} ${application.ownerLastName}`
                            : users[application.createdBy] || 'Okänd ägare')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            // En definition av var i tiden uthyrningen är (utils/rentalPhase).
                            // Förut räknade listan själv med new Date(toDate), vilket gav
                            // "Invalid Date" för tidsstämplar — raden blev aldrig avslutad.
                            const badge = rentalBadge(application);
                            const isCompleted = badge === 'ENDED';
                            const isExpiring = badge === 'EXPIRING_SOON';
                            const isActive = badge === 'ACTIVE';
                            const isUpcoming = badge === 'UPCOMING';

                            let statusClass = 'bg-blue-100 text-blue-800';
                            let statusText = application.status;

                            if (isCompleted) {
                              statusClass = 'bg-gray-100 text-gray-500';
                              statusText = 'Avslutad';
                            } else if (isExpiring) {
                              statusClass = 'bg-yellow-100 text-yellow-800';
                              statusText = 'Löper ut snart';
                            } else if (isUpcoming) {
                              statusClass = 'bg-blue-100 text-blue-800';
                              statusText = 'Kommande uthyrning';
                            } else if (isActive) {
                              statusClass = 'bg-green-100 text-green-600';
                              statusText = 'Aktiv uthyrning';
                            } else if (application.status === 'APPROVED') {
                              statusClass = 'bg-purple-100 text-purple-600';
                              statusText = 'Väntar på hyresgäst';
                            } else if (application.status === 'PENDING') {
                              statusClass = 'bg-yellow-100 text-yellow-800';
                              statusText = 'Väntar på svar';
                            }

                            return (
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}`}>
                                {statusText}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {application.fromDate && application.toDate 
                            ? `${new Date(application.fromDate).toLocaleDateString('sv-SE')} - ${new Date(application.toDate).toLocaleDateString('sv-SE')}`
                            : 'Ej angiven'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditApplication(application)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Redigera uthyrning"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteApplication(application)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Ta bort uthyrning"
                            >
                              <Trash2 size={16} />
                            </button>
                            <button
                              onClick={() => setAttachmentsModal({ open: true, application })}
                              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors relative"
                              title="Bilagor"
                            >
                              <Paperclip size={16} />
                              {application.attachments?.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-[#62B8E9] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                  {application.attachments.length}
                                </span>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Inga godkända uthyrningar för denna förening
              </div>
            )}
          </div>
        )}

        {activeTab === 'aktiveringar' && (() => {
          const search = activationSearch.trim().toLowerCase();
          const list = activatedProfiles
            .filter(p => !search || `${p.firstName} ${p.lastName} ${p.address} ${p.email}`.toLowerCase().includes(search))
            .sort((a, b) => (b.activation?.at || '').localeCompare(a.activation?.at || ''));
          const confirmed = activatedProfiles.filter(p => p.brfInfoConsent?.at).length;
          const fmt = (iso) => iso ? new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
          return (
            <div>
              <H2 className="mb-4">Aktiveringar via hyria.se</H2>
              <div className="flex gap-8 mb-6">
                <div><div className="text-2xl font-semibold">{activatedProfiles.length}</div><div className="text-xs text-gray-500">Aktiverade</div></div>
                <div><div className="text-2xl font-semibold">{confirmed}</div><div className="text-xs text-gray-500">Bekräftat informationen</div></div>
                {association.numberOfApartments > 0 && (
                  <div><div className="text-2xl font-semibold">{Math.round((activatedProfiles.length / association.numberOfApartments) * 100)} %</div><div className="text-xs text-gray-500">av {association.numberOfApartments} lägenheter</div></div>
                )}
              </div>
              <div className="relative max-w-md mb-4">
                <input type="text" placeholder="Sök namn, adress eller e-post" value={activationSearch} onChange={(e) => setActivationSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#62B8E9] focus:border-transparent outline-none" />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              {list.length === 0 ? (
                <p className="text-gray-500 py-8 text-center">{search ? 'Ingen matchar sökningen' : 'Ingen har aktiverat sig ännu'}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50"><tr>
                      {['Namn', 'Adress', 'E-post', 'Telefon', 'Aktiverad', 'Bekräftat', 'Adress från', 'Lägenhet i registret'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {list.map(p => (
                        <tr key={p.id} className={p.matchedHousingId ? '' : 'bg-amber-50'}>
                          <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{p.firstName} {p.lastName}</td>
                          <td className="px-4 py-3 text-gray-600">{p.address}{p.apartmentNumber ? `, lgh ${p.apartmentNumber}` : ''}<br /><span className="text-xs text-gray-400">{p.zipCode} {p.city}</span></td>
                          <td className="px-4 py-3 text-gray-600">{p.email || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{p.phoneNumber || '—'}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmt(p.activation?.at)}</td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.brfInfoConsent?.at ? `${fmt(p.brfInfoConsent.at)} (v ${p.brfInfoConsent.version})` : '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{p.addressSource === 'manual' ? 'Manuell' : 'Folkbokföring'}</td>
                          <td className="px-4 py-3">{p.matchedHousingId ? <span className="text-green-700">Ja</span> : <span className="text-amber-700">Nej</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'avvikelserapport' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-[#222222] mb-4">Avvikelserapporter</h2>

            {auditOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Inga beställningar ännu.</p>
            ) : (
              <div className="space-y-4">
                {auditOrders.map(order => {
                  const isCompleted = order.status === 'completed';
                  const dateStr = order.requestedAt ? new Date(order.requestedAt).toLocaleDateString('sv-SE') : '';
                  const completedDateStr = order.completedAt ? new Date(order.completedAt).toLocaleDateString('sv-SE') : '';

                  return (
                    <div key={order.id} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {isCompleted ? (
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-[#222222]">Avvikelserapport</span>
                          {isCampaignOrder(order) && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full whitespace-nowrap">
                              Gratis (kampanj)
                            </span>
                          )}
                          <p className="text-sm text-[#626262]">
                            Beställd {dateStr}
                            {isCompleted && ` — Klar ${completedDateStr}`}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {isCompleted ? (
                          <>
                            <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Uppladdad</span>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={order.invoiced || false}
                                onChange={async (e) => {
                                  const checked = e.target.checked;
                                  await updateDoc(doc(db, 'auditReportOrders', order.id), {
                                    invoiced: checked,
                                    invoicedAt: checked ? new Date().toISOString() : null
                                  });
                                  setAuditOrders(prev => prev.map(o =>
                                    o.id === order.id ? { ...o, invoiced: checked } : o
                                  ));
                                }}
                                className="w-4 h-4 text-green-600 rounded"
                              />
                              <span className={order.invoiced ? 'text-green-700 font-medium' : 'text-zinc-500'}>Fakturerad</span>
                            </label>
                            {order.reportUrl && (
                              <a
                                href={order.reportUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-2 bg-[#62B8E9] text-white rounded-lg text-sm font-medium hover:bg-[#4A9FD1] flex items-center gap-2"
                              >
                                <Download className="w-4 h-4" />
                                Ladda ner
                              </a>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="px-3 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">Väntar</span>
                            <label className={`px-3 py-2 bg-[#62B8E9] text-white rounded-lg text-sm font-medium hover:bg-[#4A9FD1] flex items-center gap-2 cursor-pointer ${uploadingOrderId === order.id ? 'opacity-50 pointer-events-none' : ''}`}>
                              <Upload className="w-4 h-4" />
                              {uploadingOrderId === order.id ? 'Laddar upp...' : 'Ladda upp rapport'}
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  setUploadingOrderId(order.id);
                                  try {
                                    const storagePath = `audit-report-orders/${order.id}/${Date.now()}_${file.name}`;
                                    const storageRef = ref(storage, storagePath);
                                    await uploadBytes(storageRef, file);
                                    const reportUrl = await getDownloadURL(storageRef);

                                    // Hämta BRF-admins för unreadBy
                                    const adminsSnap = await getDocs(query(
                                      collection(db, 'users'),
                                      where('associationId', '==', associationId),
                                      where('role', '==', 'brfadmin')
                                    ));
                                    const unreadBy = {};
                                    adminsSnap.docs.forEach(d => { unreadBy[d.id] = true; });

                                    await updateDoc(doc(db, 'auditReportOrders', order.id), {
                                      status: 'completed',
                                      completedAt: new Date().toISOString(),
                                      completedBy: auth.currentUser?.uid || null,
                                      reportUrl,
                                      reportPath: storagePath,
                                      reportFileName: file.name,
                                      unreadBy
                                    });

                                    // Skicka notis till BRF
                                    try {
                                      const functions = getFunctions(undefined, 'us-central1');
                                      const notifyFn = httpsCallable(functions, 'notifyAuditReportUploaded');
                                      await notifyFn({ orderId: order.id });
                                    } catch (notifyErr) {
                                      console.error('Notification error:', notifyErr);
                                    }

                                    toast({ type: 'success', message: 'Rapport uppladdad!' });
                                    // Uppdatera lokal state
                                    setAuditOrders(prev => prev.map(o =>
                                      o.id === order.id ? { ...o, status: 'completed', completedAt: new Date().toISOString(), reportUrl, reportFileName: file.name } : o
                                    ));
                                  } catch (err) {
                                    console.error('Upload error:', err);
                                    toast({ type: 'error', message: 'Kunde inte ladda upp rapport' });
                                  } finally {
                                    setUploadingOrderId(null);
                                    e.target.value = '';
                                  }
                                }}
                              />
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Application Modal */}
      <EditApplicationModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, application: null })}
        application={editModal.application}
        onSuccess={handleModalSuccess}
      />

      {/* Delete Application Modal */}
      <DeleteApplicationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, application: null })}
        application={deleteModal.application}
        onSuccess={handleModalSuccess}
      />

      {/* Add Rental Modal */}
      <AddRentalModal
        open={addRentalModal}
        onClose={() => setAddRentalModal(false)}
        associationId={associationId}
        onSuccess={() => {
          fetchAssociationData();
          toast({ type: 'success', message: 'Uthyrning skapad framgångsrikt!' });
        }}
      />

      {/* Edit Apartment Modal */}
      <EditApartmentModal
        open={editApartmentModal.open}
        onClose={() => setEditApartmentModal({ open: false, apartment: null })}
        apartment={editApartmentModal.apartment}
        onSuccess={() => {
          fetchAssociationData();
          toast({ type: 'success', message: 'Lägenhet uppdaterad' });
        }}
      />

      {/* Add Apartment Modal */}
      <EditApartmentModal
        open={addApartmentModal}
        onClose={() => setAddApartmentModal(false)}
        mode="add"
        associationId={associationId}
        onSuccess={() => {
          fetchAssociationData();
          toast({ type: 'success', message: 'Medlem tillagd' });
        }}
      />

      {/* Attachments Modal */}
      <Dialog
        open={attachmentsModal.open}
        onClose={() => setAttachmentsModal({ open: false, application: null })}
        className="fixed z-50 inset-0 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg mx-auto max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#222222] font-['Poppins']">
              Bilagor — {attachmentsModal.application?.address || ''}
            </h2>
            <button
              onClick={() => setAttachmentsModal({ open: false, application: null })}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              &times;
            </button>
          </div>
          {attachmentsModal.application && (
            <AttachmentsSection
              applicationId={attachmentsModal.application.id}
              attachments={attachmentsModal.application.attachments || []}
              canUpload={true}
              onAttachmentsChange={(newAttachments) => {
                setAttachmentsModal(prev => ({
                  ...prev,
                  application: { ...prev.application, attachments: newAttachments }
                }));
                setApplications(prev => prev.map(app =>
                  app.id === attachmentsModal.application.id
                    ? { ...app, attachments: newAttachments }
                    : app
                ));
              }}
            />
          )}
        </div>
      </Dialog>

      {/* Delete Association Modal */}
      <Dialog
        open={deleteAssociationModal}
        onClose={() => {
          if (!deletingAssociation) {
            setDeleteAssociationModal(false);
            setDeleteConfirmText('');
          }
        }}
        className="fixed z-50 inset-0 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg mx-auto">
          {/* Header */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Ta bort förening permanent?
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Denna åtgärd kan inte ångras.
              </p>
            </div>
          </div>

          {/* Warning content */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800 font-medium mb-2">
              Följande kommer att raderas permanent:
            </p>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• Föreningen: <strong>{association?.name}</strong></li>
              <li>• {housing.length} lägenheter</li>
              <li>• {applications.length} ansökningar</li>
              <li>• Alla kopplade BRF-administratörer</li>
            </ul>
          </div>

          {/* Confirmation input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Skriv föreningens namn för att bekräfta:
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={association?.name}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              disabled={deletingAssociation}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                setDeleteAssociationModal(false);
                setDeleteConfirmText('');
              }}
              disabled={deletingAssociation}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              onClick={handleDeleteAssociation}
              disabled={deletingAssociation || deleteConfirmText !== association?.name}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deletingAssociation ? 'Raderar...' : 'Ta bort permanent'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Delete Member Confirmation Modal */}
      <Dialog
        open={deleteMemberModal.open}
        onClose={() => {
          if (!deletingMember) setDeleteMemberModal({ open: false, apartment: null });
        }}
        className="fixed z-50 inset-0 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-md mx-auto">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Ta bort medlem?</h2>
              <p className="text-sm text-gray-600 mt-1">
                Medlemmen tas bort ur förteckningen. Hens ansökningar och historik ligger kvar, markerade som arkiverade.
              </p>
            </div>
          </div>

          {deleteMemberModal.apartment && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm">
              <div className="font-medium text-gray-900">
                {memberDisplayName(deleteMemberModal.apartment.member) || 'Okänd medlem'}
              </div>
              <div className="text-gray-600">
                Lgh {deleteMemberModal.apartment.apartmentNumber} — {deleteMemberModal.apartment.address}
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800">
            Inget raderas. Ansökningarna syns under &quot;Visa arkiverade&quot; i ansökningslistan, och medlemmen finns kvar i lägenhetens historik.
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setDeleteMemberModal({ open: false, apartment: null })}
              disabled={deletingMember}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              onClick={handleDeleteMember}
              disabled={deletingMember}
              className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {deletingMember ? 'Tar bort...' : 'Ta bort'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
